import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, runJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PRIORITY_META, TYPE_META, fmtDate } from "@/lib/brand";
import { Plus, FolderKanban, Trash2, ArrowUpRight, X, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700",
  on_hold: "bg-amber-50 text-amber-700",
  completed: "bg-slate-100 text-slate-600",
};

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClient = user.role === "client";
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", client_id: "", due_date: "" });
  const [tasks, setTasks] = useState([{ title: "", priority: "medium" }]);

  // --- Import from document ---
  const [importOpen, setImportOpen] = useState(false);
  const [importStage, setImportStage] = useState("upload"); // upload | review
  const [importClientId, setImportClientId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importName, setImportName] = useState("");
  const [importTasks, setImportTasks] = useState([]);
  const [importDest, setImportDest] = useState("new"); // new | existing
  const [importExistingId, setImportExistingId] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [creatingImport, setCreatingImport] = useState(false);

  const resetImport = () => {
    setImportStage("upload"); setImportFile(null); setImportName("");
    setImportTasks([]); setParsing(false); setImportDest("new"); setImportExistingId(""); setDragOver(false);
  };

  const doParse = async () => {
    if (!importFile) return toast.error("Choose a file first");
    if (!isClient && !importClientId) return toast.error("Select a client");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      if (!isClient) fd.append("client_id", importClientId);
      const result = await runJob(
        api.post("/imports/parse", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        { interval: 2500, timeout: 180000 }
      );
      if (!result.tasks || result.tasks.length === 0) {
        toast.error("No tasks could be extracted from this document");
        setParsing(false);
        return;
      }
      setImportName(result.suggested_name || "Imported Project");
      setImportTasks(result.tasks.map((t) => ({ ...t, selected: true })));
      setImportStage("review");
    } catch (e) {
      toast.error(e.message || "Could not parse document");
    } finally {
      setParsing(false);
    }
  };

  const updateImportTask = (i, k, v) => setImportTasks((arr) => arr.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const doCreateImport = async () => {
    const chosen = importTasks.filter((t) => t.selected && t.title.trim());
    if (chosen.length === 0) return toast.error("Select at least one task");
    const payloadTasks = chosen.map((t) => ({ title: t.title, description: t.description || "", priority: t.priority, type: t.type }));
    setCreatingImport(true);
    try {
      if (importDest === "existing") {
        if (!importExistingId) { setCreatingImport(false); return toast.error("Choose a project to add to"); }
        await api.post(`/projects/${importExistingId}/tasks`, { tasks: payloadTasks });
        toast.success(`Added ${chosen.length} task(s) to project`);
      } else {
        if (!importName.trim()) { setCreatingImport(false); return toast.error("Project name is required"); }
        await api.post("/projects", {
          name: importName,
          client_id: isClient ? undefined : importClientId,
          tasks: payloadTasks,
        });
        toast.success(`Project created with ${chosen.length} task(s)`);
      }
      setImportOpen(false);
      resetImport();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreatingImport(false);
    }
  };

  const load = useCallback(() => api.get("/projects").then((r) => setProjects(r.data)), []);
  useEffect(() => {
    load();
    if (!isClient) api.get("/clients").then((r) => setClients(r.data));
  }, [load, isClient]);

  const addTaskRow = () => setTasks([...tasks, { title: "", priority: "medium" }]);
  const updateTask = (i, k, v) => setTasks(tasks.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const removeTask = (i) => setTasks(tasks.filter((_, idx) => idx !== i));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    if (!isClient && !form.client_id) return toast.error("Select a client");
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, due_date: form.due_date || null,
        client_id: isClient ? undefined : form.client_id,
        tasks: tasks.filter((t) => t.title.trim()).map((t) => ({ title: t.title, priority: t.priority, type: "task" })),
      };
      await api.post("/projects", payload);
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", description: "", client_id: "", due_date: "" });
      setTasks([{ title: "", priority: "medium" }]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Projects</h1>
          <p className="text-slate-500 text-sm mt-1">{projects.length} project plan{projects.length === 1 ? "" : "s"} · group tasks &amp; track delivery</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) resetImport(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-project-btn">
                <Upload size={16} className="mr-1.5" /> Import
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">Import project from a document</DialogTitle>
              </DialogHeader>

              {importStage === "upload" && (
                <div className="space-y-4">
                  {!isClient && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Client</Label>
                      <Select value={importClientId} onValueChange={setImportClientId}>
                        <SelectTrigger className="mt-1.5" data-testid="import-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                        <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Document</Label>
                    <label
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault(); setDragOver(false);
                        const f = e.dataTransfer.files?.[0];
                        if (f) setImportFile(f);
                      }}
                      className={`mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-8 cursor-pointer transition-colors ${dragOver ? "border-[color:var(--tenant-primary)] bg-orange-50/40" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="import-dropzone">
                      <FileText size={26} className={dragOver ? "text-[color:var(--tenant-primary)]" : "text-slate-400"} />
                      <span className="text-sm text-slate-600">{importFile ? importFile.name : (dragOver ? "Drop the file here" : "Drag & drop or click to choose a file")}</span>
                      <span className="text-[11px] text-slate-400">Excel, Word, PDF, CSV or TXT · AI will suggest a task list</span>
                      <input type="file" accept=".xlsx,.docx,.pdf,.csv,.txt,.md" className="hidden"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
                    </label>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={doParse} disabled={parsing} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="parse-import-btn">
                      {parsing ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Reading document…</> : "Parse document"}
                    </Button>
                  </div>
                </div>
              )}

              {importStage === "review" && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <button onClick={() => setImportDest("new")} data-testid="dest-new-btn"
                      className={`flex-1 text-sm rounded-md border px-3 py-2 transition-colors ${importDest === "new" ? "border-[color:var(--tenant-primary)] bg-orange-50/40 text-slate-800 font-medium" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      New project
                    </button>
                    <button onClick={() => setImportDest("existing")} data-testid="dest-existing-btn"
                      className={`flex-1 text-sm rounded-md border px-3 py-2 transition-colors ${importDest === "existing" ? "border-[color:var(--tenant-primary)] bg-orange-50/40 text-slate-800 font-medium" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      Add to existing
                    </button>
                  </div>
                  {importDest === "new" ? (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Project name</Label>
                      <Input value={importName} onChange={(e) => setImportName(e.target.value)} className="mt-1.5" data-testid="import-name-input" />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Add tasks to project</Label>
                      <Select value={importExistingId} onValueChange={setImportExistingId}>
                        <SelectTrigger className="mt-1.5" data-testid="import-existing-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                        <SelectContent>
                          {projects.filter((p) => isClient || !importClientId || p.client_id === importClientId).map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}{!isClient ? ` — ${p.client_name}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Review the extracted tasks — untick any you don't want, edit titles/priority/type.</p>
                  <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
                    {importTasks.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid="import-task-row">
                        <input type="checkbox" checked={t.selected} onChange={(e) => updateImportTask(i, "selected", e.target.checked)} data-testid="import-task-check" className="h-4 w-4 accent-[color:var(--tenant-primary)]" />
                        <Input value={t.title} onChange={(e) => updateImportTask(i, "title", e.target.value)} className="flex-1 h-8 text-sm" data-testid="import-task-title" />
                        <Select value={t.priority} onValueChange={(v) => updateImportTask(i, "priority", v)}>
                          <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={t.type} onValueChange={(v) => updateImportTask(i, "type", v)}>
                          <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={resetImport} data-testid="import-back-btn">Back</Button>
                    <Button onClick={doCreateImport} disabled={creatingImport} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="create-import-btn">
                      {creatingImport ? "Saving…" : importDest === "existing"
                        ? `Add ${importTasks.filter((t) => t.selected).length} task(s)`
                        : `Create Project (${importTasks.filter((t) => t.selected).length})`}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="new-project-btn">
              <Plus size={16} className="mr-1" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Create a project plan</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {!isClient && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="project-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Project name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="project-name-input" placeholder="e.g. Q3 SEO Growth Plan" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" data-testid="project-desc-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" data-testid="project-due-input" /></div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Tasks</Label>
                <div className="space-y-2 mt-1.5">
                  {tasks.map((t, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid="project-task-row">
                      <Input value={t.title} onChange={(e) => updateTask(i, "title", e.target.value)} placeholder={`Task ${i + 1}`} className="flex-1" data-testid="task-title-input" />
                      <Select value={t.priority} onValueChange={(v) => updateTask(i, "priority", v)}>
                        <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {tasks.length > 1 && <button onClick={() => removeTask(i)} className="text-slate-400 hover:text-red-600"><X size={16} /></button>}
                    </div>
                  ))}
                </div>
                <button onClick={addTaskRow} className="text-xs text-sky-600 hover:underline mt-2 flex items-center gap-1" data-testid="add-task-row-btn"><Plus size={13} /> Add task</button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-project-btn">
                {saving ? "Creating…" : "Create Project"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((p) => {
          const pct = p.task_count ? Math.round((p.done_count / p.task_count) * 100) : 0;
          return (
            <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid="project-card"
              className="p-5 border-slate-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
              <div className="flex items-center gap-2">
                <FolderKanban size={18} style={{ color: "var(--tenant-primary)" }} />
                <h3 className="font-display font-semibold text-slate-900 truncate flex-1">{p.name}</h3>
                <ArrowUpRight size={16} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
              </div>
              {!isClient && <p className="text-xs text-slate-400 mt-1">{p.client_name}</p>}
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.description || "No description."}</p>
              <div className="mt-4">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">{p.done_count}/{p.task_count} tasks done</span>
                  <span className="text-slate-400">{pct}%</span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_CLS[p.status] || STATUS_CLS.active}`}>{p.status.replace("_", " ")}</span>
                <span className="text-[11px] text-slate-400">Due {fmtDate(p.due_date)}</span>
              </div>
            </Card>
          );
        })}
        {projects.length === 0 && <p className="text-sm text-slate-400 col-span-full text-center py-10">No projects yet. Create your first project plan.</p>}
      </div>
    </div>
  );
}
